import React from 'react';
import { Calculator, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров пенсионного калькулятора
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущей зарплаты
  const salary = parseFloat(formData.salaryAmount) || 0;
  
  // Расчет текущих накоплений
  const savings = parseFloat(formData.savingsAmount) || 0;
  
  // Расчет ежемесячного взноса
  const monthlyContribution = parseFloat(formData.monthlyContribution) || 0;
  
  // Обработчик изменения зарплаты
  const handleSalaryChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      salaryAmount: newValue
    }));
  };
  
  // Обработчик изменения накоплений
  const handleSavingsChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      savingsAmount: newValue
    }));
  };
  
  // Обработчик изменения ежемесячного взноса
  const handleMonthlyContributionChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      monthlyContribution: newValue
    }));
  };
  
  // Обработчик изменения роста зарплаты
  const handleAnnualIncreaseRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      annualIncreaseRate: newValue
    }));
  };
  
  // Предопределенные значения зарплаты для быстрого выбора
  const predefinedSalaries = [
    { value: '50000', label: '50 тыс.' },
    { value: '80000', label: '80 тыс.' },
    { value: '120000', label: '120 тыс.' },
    { value: '200000', label: '200 тыс.' }
  ];
  
  // Предопределенные значения накоплений для быстрого выбора
  const predefinedSavings = [
    { value: '0', label: '0' },
    { value: '100000', label: '100 тыс.' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];
  
  // Предопределенные значения ежемесячных взносов для быстрого выбора
  const predefinedContributions = [
    { value: '0', label: '0' },
    { value: '3000', label: '3 тыс.' },
    { value: '5000', label: '5 тыс.' },
    { value: '10000', label: '10 тыс.' }
  ];
  
  // Функция расчета
  const calculatePension = () => {
    // Эмулируем нажатие на кнопку "Рассчитать пенсию"
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Зарплата */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="salarySlider" className="text-gray-600 dark:text-gray-400">Ежемесячная зарплата</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(salary, 0)}</span>
          </div>
          <input
            type="range"
            id="salarySlider"
            min="10000"
            max="300000"
            step="5000"
            value={salary}
            onChange={handleSalaryChange}
            aria-label="Ежемесячная зарплата"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedSalaries.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, salaryAmount: item.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.salaryAmount === item.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Накопления */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="savingsSlider" className="text-gray-600 dark:text-gray-400">Текущие накопления</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(savings, 0)}</span>
          </div>
          <input
            type="range"
            id="savingsSlider"
            min="0"
            max="3000000"
            step="50000"
            value={savings}
            onChange={handleSavingsChange}
            aria-label="Текущие накопления"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedSavings.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, savingsAmount: item.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.savingsAmount === item.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ежемесячный взнос */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="contributionSlider" className="text-gray-600 dark:text-gray-400">Ежемесячный взнос</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(monthlyContribution, 0)}</span>
          </div>
          <input
            type="range"
            id="contributionSlider"
            min="0"
            max="30000"
            step="1000"
            value={monthlyContribution}
            onChange={handleMonthlyContributionChange}
            aria-label="Ежемесячный взнос на пенсию"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedContributions.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, monthlyContribution: item.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.monthlyContribution === item.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Рост зарплаты */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="increaseRateSlider" className="text-gray-600 dark:text-gray-400">Ежегодный рост ЗП (%)</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.annualIncreaseRate}%</span>
          </div>
          <input
            type="range"
            id="increaseRateSlider"
            min="0"
            max="15"
            step="0.5"
            value={formData.annualIncreaseRate}
            onChange={handleAnnualIncreaseRateChange}
            aria-label="Ежегодный рост зарплаты"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Предопределенные значения роста зарплаты */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['0', '3', '5', '7'].map(rate => (
              <button
                key={rate}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, annualIncreaseRate: rate }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.annualIncreaseRate === rate 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип пенсии */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип пенсии</label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pensionType: 'mandatory' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pensionType === 'mandatory' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Страховая</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pensionType: 'voluntary' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pensionType === 'voluntary' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Накопит.</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, pensionType: 'both' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.pensionType === 'both' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Смешан.</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculatePension}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать пенсию
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;